import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { getActiveViewFromPath } from "@/constants/navigation-routes";
import { VIEW, isPolicyView } from "@/constants/tabs";
import type { ActiveView } from "@/constants/tabs";

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconGrid() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <rect x="3" y="3" width="7" height="7" rx="1" strokeWidth={1.5} />
            <rect x="14" y="3" width="7" height="7" rx="1" strokeWidth={1.5} />
            <rect x="3" y="14" width="7" height="7" rx="1" strokeWidth={1.5} />
            <rect x="14" y="14" width="7" height="7" rx="1" strokeWidth={1.5} />
        </svg>
    );
}

function IconUser() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" d="M12 4a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm-6 14a6 6 0 0 1 12 0" />
        </svg>
    );
}

function IconGroup() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path
                strokeLinecap="round"
                d="M17 20c0-2.21-2.24-4-5-4s-5 1.79-5 4M12 12a3 3 0 1 1 0-6 3 3 0 0 1 0 6zm6.5 0a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M20 18c0-1.38-1.12-2.5-2.5-2.5"
            />
        </svg>
    );
}

function IconShield() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
        </svg>
    );
}

function IconLock() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
        </svg>
    );
}

function IconDoc() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6z" />
            <path d="M14 3v6h6M8 13h8M8 17h5" strokeLinecap="round" />
        </svg>
    );
}

function IconChevronRight({ className = "" }: { className?: string }) {
    return (
        <svg className={`h-3 w-3 ${className}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M9 18l6-6-6-6" />
        </svg>
    );
}

function IconX() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
    );
}

function IconFolder() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
            <path strokeLinecap="round" d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
        </svg>
    );
}

function IconMenu() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
        </svg>
    );
}

function IconWarning() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        </svg>
    );
}

function IconMapPin() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 21s7-4.35 7-11a7 7 0 1 0-14 0c0 6.65 7 11 7 11z" />
            <circle cx="12" cy="10" r="2.5" />
        </svg>
    );
}

function IconDollar() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
    );
}

function IconBuilding() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 22v-6.57" />
            <path d="M12 11h.01" />
            <path d="M12 7h.01" />
            <path d="M14 15.43V22" />
            <path d="M15 16a5 5 0 0 0-6 0" />
            <path d="M16 11h.01" />
            <path d="M16 7h.01" />
            <path d="M8 11h.01" />
            <path d="M8 7h.01" />
            <rect x="4" y="2" width="16" height="20" rx="2" />
        </svg>
    );
}

function IconReceipt() {
    return (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
            <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
            <path d="M12 17.5v-11" />
        </svg>
    );
}

// ─── NavItem ─────────────────────────────────────────────────────────────────

function NavItem({
    icon,
    label,
    active = false,
    indent = false,
    collapsed = false,
    collapsible = false,
    expanded = false,
    controlsId,
    onClick,
    disabled = false,
    labelClassName = "",
    renderChildrenWhenCollapsed = false,
    children,
}: {
    icon?: React.ReactNode;
    label: string;
    active?: boolean;
    indent?: boolean;
    collapsed?: boolean;
    collapsible?: boolean;
    expanded?: boolean;
    controlsId?: string;
    onClick?: () => void;
    disabled?: boolean;
    labelClassName?: string;
    renderChildrenWhenCollapsed?: boolean;
    children?: React.ReactNode;
}) {
    const hasNestedItems = children !== undefined && children !== null && children !== false;
    const isCollapsible = collapsible || hasNestedItems;
    const shouldRenderChildren = hasNestedItems && expanded && (!collapsed || renderChildrenWhenCollapsed);

    return (
        <>
            <button
                type="button"
                title={label}
                aria-expanded={isCollapsible ? expanded : undefined}
                aria-controls={isCollapsible ? controlsId : undefined}
                aria-disabled={disabled || undefined}
                disabled={disabled}
                onClick={onClick}
                className={`flex w-full items-center rounded-md px-3 py-2 text-sm transition ${collapsed ? "justify-center" : `gap-3 ${indent ? "pl-9" : ""}`} ${
                    disabled ? "cursor-not-allowed text-blue-300/60" : active ? "bg-white/20 font-semibold text-white" : "text-blue-100 hover:bg-white/10"
                }`}
            >
                {icon && <span className="shrink-0">{icon}</span>}
                {!collapsed && <span className={`flex-1 text-left ${labelClassName}`}>{label}</span>}
                {!collapsed && isCollapsible && <IconChevronRight className={`transition-transform ${expanded ? "rotate-90" : "rotate-0"}`} />}
            </button>
            {shouldRenderChildren && children}
        </>
    );
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

export interface SidebarProps {
    isCollapsed: boolean;
    isMobile: boolean;
    isMobileOpen: boolean;
    allowedViews: Set<ActiveView>;
    showUtilities: boolean;
    /** When true the sidebar is shown but non-interactive (e.g. while signing out). */
    disabled?: boolean;
    onToggle: () => void;
    onViewChange: (view: ActiveView) => void;
}

export function Sidebar({ isCollapsed, isMobile, isMobileOpen, allowedViews, showUtilities, disabled = false, onToggle, onViewChange }: SidebarProps) {
    const location = useLocation();
    const activeView = getActiveViewFromPath(location.pathname);

    const [isManageOpen, setIsManageOpen] = useState(true);
    const [isPolicyOpen, setIsPolicyOpen] = useState(() => activeView != null && isPolicyView(activeView));
    const [isWorkflowsOpen, setIsWorkflowsOpen] = useState(false);

    useEffect(() => {
        if (activeView != null && isPolicyView(activeView)) {
            setIsPolicyOpen(true);
        }
    }, [activeView]);

    const sidebarWidthClass = isMobile ? "w-56" : isCollapsed ? "w-[72px]" : "w-56";
    const mobilePositionClass = isMobile ? (isMobileOpen ? "translate-x-0" : "-translate-x-full") : "translate-x-0";

    const can = (view: ActiveView) => allowedViews.has(view);

    const policyItems = [
        { view: VIEW.PolicyExceptions, label: "Exceptions", icon: <IconWarning /> },
        // TODO: Temporarily disable Mileage, Currency, and Hotel options; re-enable these once the related flows are ready again.
        { view: VIEW.PolicyMileage, label: "Mileage", icon: <IconMapPin />, disabled: true },
        { view: VIEW.PolicyCurrency, label: "Currency", icon: <IconDollar />, disabled: true },
        { view: VIEW.PolicyHotel, label: "Hotel", icon: <IconBuilding />, disabled: true },
        { view: VIEW.PolicyReceipt, label: "Receipt", icon: <IconReceipt /> },
    ].filter((item) => can(item.view));
    const showPolicySection = policyItems.length > 0;

    const manageItems = [
        { view: VIEW.Approval, label: "Approval", icon: <IconDoc /> },
        { view: VIEW.Categories, label: "Categories", icon: <IconGrid /> },
    ].filter((item) => can(item.view));
    const showManageSection = manageItems.length > 0;

    return (
        <aside
            aria-disabled={disabled || undefined}
            // `inert` blocks pointer AND keyboard interaction while signing out.
            inert={disabled || undefined}
            className={`fixed inset-y-0 left-0 z-50 flex h-full flex-col bg-blue-700 text-white shadow-2xl transition-all duration-300 md:relative md:z-auto md:shadow-none ${sidebarWidthClass} ${mobilePositionClass} ${disabled ? "opacity-60" : ""}`}
        >
            {/* Logo */}
            <div className={`flex items-center px-4 py-4 ${isCollapsed ? "justify-center" : "justify-between"}`}>
                {!isCollapsed && <div className="flex h-7 w-7 items-center justify-center rounded bg-white/10 text-sm font-bold">ITS</div>}
                <button
                    type="button"
                    aria-label={isCollapsed ? "Open menu" : "Collapse menu"}
                    className="rounded p-1 text-blue-100 hover:bg-white/10"
                    onClick={onToggle}
                >
                    {isCollapsed ? <IconMenu /> : <IconX />}
                </button>
            </div>

            {/* Section: Expense Admin */}
            <div className="px-3 pb-1">
                {!isCollapsed && <p className="px-3 py-1.5 text-[10px] font-semibold tracking-wide text-blue-300 uppercase">Expense Admin</p>}
                <nav className="flex flex-col gap-0.5">
                    {can(VIEW.Dashboard) ? (
                        <NavItem
                            icon={<IconGrid />}
                            label="Dashboard"
                            active={activeView === VIEW.Dashboard}
                            collapsed={isCollapsed}
                            onClick={() => onViewChange(VIEW.Dashboard)}
                        />
                    ) : null}
                    {can(VIEW.Users) ? (
                        <NavItem
                            icon={<IconUser />}
                            label="Users"
                            active={activeView === VIEW.Users}
                            collapsed={isCollapsed}
                            onClick={() => onViewChange(VIEW.Users)}
                        />
                    ) : null}
                    {can(VIEW.Groups) ? (
                        <NavItem
                            icon={<IconGroup />}
                            label="Groups"
                            active={activeView === VIEW.Groups}
                            collapsed={isCollapsed}
                            onClick={() => onViewChange(VIEW.Groups)}
                        />
                    ) : null}
                    {can(VIEW.Permissions) ? (
                        <NavItem
                            icon={<IconShield />}
                            label="Permissions"
                            active={activeView === VIEW.Permissions}
                            collapsed={isCollapsed}
                            onClick={() => onViewChange(VIEW.Permissions)}
                        />
                    ) : null}
                    {showPolicySection ? (
                        <NavItem
                            icon={<IconDoc />}
                            label="Policy"
                            collapsed={isCollapsed}
                            collapsible
                            expanded={isPolicyOpen}
                            controlsId="policy-submenu"
                            onClick={() => setIsPolicyOpen((prev) => !prev)}
                        >
                            <nav id="policy-submenu" className="flex flex-col gap-0.5">
                                {policyItems.map((item) => (
                                    <NavItem
                                        key={item.view}
                                        icon={item.icon}
                                        label={item.label}
                                        indent
                                        collapsed={isCollapsed}
                                        active={activeView === item.view}
                                        onClick={() => onViewChange(item.view)}
                                        disabled={item.disabled}
                                    />
                                ))}
                            </nav>
                        </NavItem>
                    ) : null}
                </nav>
            </div>

            {/* Section: Manage */}
            {showManageSection ? (
                <div className="px-3 pt-3 pb-1">
                    <NavItem
                        icon={<IconFolder />}
                        label="Manage"
                        collapsed={isCollapsed}
                        collapsible
                        expanded={isManageOpen}
                        controlsId="manage-submenu"
                        onClick={() => setIsManageOpen((prev) => !prev)}
                        labelClassName="font-medium"
                        renderChildrenWhenCollapsed
                    >
                        <nav id="manage-submenu" className="flex flex-col gap-0.5">
                            {manageItems.map((item) => (
                                <NavItem
                                    key={item.view}
                                    icon={item.icon}
                                    label={item.label}
                                    active={activeView === item.view}
                                    indent
                                    collapsed={isCollapsed}
                                    onClick={() => onViewChange(item.view)}
                                />
                            ))}
                            <NavItem icon={<IconGroup />} label="Custom data" indent collapsed={isCollapsed} disabled />
                            <NavItem
                                icon={<IconGrid />}
                                label="Workflows"
                                indent
                                collapsed={isCollapsed}
                                collapsible
                                expanded={isWorkflowsOpen}
                                controlsId="workflows-submenu"
                                onClick={() => setIsWorkflowsOpen((prev) => !prev)}
                                disabled
                            >
                                <nav id="workflows-submenu" className="flex flex-col gap-0.5">
                                    <NavItem icon={<IconDoc />} label="Active Workflows" indent collapsed={isCollapsed} disabled />
                                    <NavItem icon={<IconDoc />} label="Workflow Templates" indent collapsed={isCollapsed} disabled />
                                </nav>
                            </NavItem>
                        </nav>
                    </NavItem>
                </div>
            ) : null}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Bottom nav */}
            <div className="px-3 pt-2 pb-3">
                {showUtilities ? <NavItem icon={<IconGrid />} label="Utilities" collapsed={isCollapsed} disabled /> : null}
                <NavItem icon={<IconLock />} label="Help" collapsed={isCollapsed} disabled />
            </div>
        </aside>
    );
}
